import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Modal, Popconfirm, Space, Spin, Tag, Typography, message } from "antd";
import {
    ArrowRightOutlined,
    DeleteOutlined,
    DownloadOutlined,
    FilePdfOutlined,
} from "@ant-design/icons";

import {
    deleteTransactionPlanInvoice,
    downloadTransactionPlanInvoice,
    viewTransactionPlanInvoice,
} from "../../api/transactionPlanApi";
import { saveBlob } from "../transactionSchedule/fileActions";
import { formatFileSize, formatPlanDate } from "./transactionPlanUi";
import { useTranslation } from "../../i18n/LanguageProvider";

const { Text } = Typography;

/**
 * Shows the invoice PDF of one Гүйлгээний төлөвлөгөө row inside a modal, without leaving the
 * list page, and offers the original file for download.
 */
function TransactionPlanInvoiceModal({ open, record, canDelete = false, onClose, onDeleted }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState("");
    const [blobUrl, setBlobUrl] = useState("");

    const planId = record?.id;

    const loadInvoice = useCallback(async (id) => {
        setLoading(true);
        setError("");

        try {
            const result = await viewTransactionPlanInvoice(id);
            const url = window.URL.createObjectURL(result.blob);
            setBlobUrl(url);
        } catch (err) {
            setError(err?.message || t("invoice.loadFailed"));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        if (!open || !planId) return undefined;

        loadInvoice(planId);

        return undefined;
    }, [open, planId, loadInvoice]);

    // The object URL lives as long as the modal shows that file.
    useEffect(() => {
        if (!blobUrl) return undefined;

        return () => {
            window.URL.revokeObjectURL(blobUrl);
        };
    }, [blobUrl]);

    useEffect(() => {
        if (!open) {
            setBlobUrl("");
            setError("");
        }
    }, [open]);

    const handleDownload = async () => {
        if (!planId) return;

        setDownloading(true);

        try {
            const result = await downloadTransactionPlanInvoice(planId);
            saveBlob(result, record?.invoiceFileName || `invoice-${planId}.pdf`);
            message.success(t("invoice.downloaded"));
        } catch (err) {
            message.error(err?.message || t("invoice.downloadFailed"));
        } finally {
            setDownloading(false);
        }
    };

    const handleDelete = async () => {
        if (!planId) return;

        setDeleting(true);

        try {
            await deleteTransactionPlanInvoice(planId);
            message.success(t("invoice.deleted"));
            onDeleted?.(planId);
            onClose?.();
        } catch (err) {
            message.error(err?.message || t("invoice.deleteFailed"));
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Modal
            title={
                <Space>
                    <FilePdfOutlined style={{ color: "#cf1322" }} />
                    <span>{t("invoice.modalTitle")}</span>
                </Space>
            }
            open={open}
            onCancel={onClose}
            width={960}
            destroyOnClose
            footer={
                <Space>
                    <Button onClick={onClose}>{t("action.close")}</Button>

                    {canDelete && record?.hasInvoice && (
                        <Popconfirm
                            title={t("invoice.deleteConfirmTitle")}
                            description={t("invoice.deleteConfirmBody")}
                            okText={t("action.delete")}
                            cancelText={t("action.cancel")}
                            okButtonProps={{ danger: true }}
                            onConfirm={handleDelete}
                        >
                            <Button danger icon={<DeleteOutlined />} loading={deleting}>
                                {t("invoice.delete")}
                            </Button>
                        </Popconfirm>
                    )}

                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        loading={downloading}
                        disabled={!record?.hasInvoice}
                        onClick={handleDownload}
                    >
                        {t("action.download")}
                    </Button>
                </Space>
            }
        >
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
                {/*
                  * A compact header rather than a bordered Descriptions grid: the grid forced
                  * every value into a narrow fixed cell, wrapping the id and cramping the file
                  * name. Here the file leads, its details sit under it as one muted line, and
                  * the row it belongs to reads as sender -> beneficiary.
                  */}
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-start gap-3">
                            <FilePdfOutlined
                                style={{ fontSize: 22, color: "#cf1322", marginTop: 2 }}
                            />
                            <div className="min-w-0">
                                <div
                                    className="truncate font-semibold text-slate-900"
                                    title={record?.invoiceFileName || ""}
                                >
                                    {record?.invoiceFileName || "-"}
                                </div>
                                <div className="mt-1 text-xs text-slate-500">
                                    {[
                                        formatFileSize(record?.invoiceFileSize),
                                        formatPlanDate(record?.invoiceUploadedAt),
                                        record?.invoiceUploadedBy,
                                    ]
                                        .filter((part) => part && part !== "-")
                                        .join("  ·  ")}
                                </div>
                            </div>
                        </div>

                        <Tag color="blue" className="shrink-0" style={{ marginInlineEnd: 0 }}>
                            {`${t("col.id")} ${record?.id ?? "-"}`}
                        </Tag>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-200 pt-3">
                        <span className="min-w-0 truncate text-sm text-slate-900" title={record?.sender || ""}>
                            {record?.sender || "-"}
                        </span>
                        <ArrowRightOutlined className="text-xs text-slate-400" />
                        <span
                            className="min-w-0 truncate text-sm text-slate-900"
                            title={record?.beneficiary || ""}
                        >
                            {record?.beneficiary || "-"}
                        </span>
                    </div>
                </div>

                {error && (
                    <Alert type="error" showIcon message={t("invoice.error")} description={error} />
                )}

                <div
                    style={{
                        border: "1px solid #f0f0f0",
                        borderRadius: 10,
                        background: "#fafafa",
                        height: 620,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                    }}
                >
                    {loading && <Spin tip={t("invoice.loading")} />}

                    {!loading && blobUrl && (
                        <iframe
                            title="transaction-plan-invoice"
                            src={blobUrl}
                            style={{ width: "100%", height: "100%", border: "none" }}
                        />
                    )}

                    {!loading && !blobUrl && !error && (
                        <Text type="secondary">{t("invoice.empty")}</Text>
                    )}
                </div>
            </Space>
        </Modal>
    );
}

export default TransactionPlanInvoiceModal;
